import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import {
  CheckCircle,
  CreditCard,
  Wallet,
  Smartphone,
  Loader2,
  AlertCircle,
} from "lucide-react";

const CheckoutModal = ({ isOpen, onClose, item, onSuccess }) => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState("select"); // select, processing, success, error
  const [selectedMethod, setSelectedMethod] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep("select");
      setSelectedMethod(null);
    }
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const amount = item.priceETB || item.price || 0;
  const itemName = item.name || item.title || "Item";

  const handlePayment = () => {
    if (!selectedMethod) return;

    setStep("processing");

    // Simulate API call
    setTimeout(() => {
      setStep("success");
      if (onSuccess) {
        onSuccess();
      }
    }, 2000);
  };

  const methods = [
    {
      id: "telebirr",
      name: "Telebirr",
      icon: Smartphone,
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
      detail: "Mobile Money",
    },
    {
      id: "inverse",
      name: "Inverse (Card)",
      icon: CreditCard,
      color: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
      detail: "Visa / Mastercard",
    },
    {
      id: "wallet",
      name: "Adnaki Wallet",
      icon: Wallet,
      color: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
      balance: currentUser?.wallet?.currentBalanceETB
        ? `${currentUser.wallet.currentBalanceETB.toLocaleString()} ETB`
        : "0 ETB",
    },
  ];

  const renderContent = () => {
    switch (step) {
      case "select":
        return (
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Payment for</p>
              <p className="font-semibold text-slate-800">{itemName}</p>
              <div className="mt-2 flex justify-between items-center border-t border-slate-200 pt-2">
                <span className="text-slate-600">Total Amount</span>
                <span className="text-xl font-bold text-primary">
                  {amount.toLocaleString()} ETB
                </span>
              </div>
            </div>

            <p className="text-sm font-medium text-slate-700">
              Select Payment Method
            </p>
            <div className="space-y-3">
              {methods.map((method) => (
                <div
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`relative flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
                    selectedMethod === method.id
                      ? `${method.border} ${method.bg} ring-1 ring-offset-1 ${method.color.replace(
                          "text",
                          "ring"
                        )}`
                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <div
                    className={`p-2 rounded-full bg-white ${method.color} mr-4 shadow-sm`}
                  >
                    <method.icon size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {method.name}
                    </p>
                    {method.balance ? (
                      <p className="text-xs text-slate-500">
                        Balance: {method.balance}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500">{method.detail}</p>
                    )}
                  </div>
                  {selectedMethod === method.id && (
                    <CheckCircle size={20} className={method.color} />
                  )}
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Button
                onClick={handlePayment}
                disabled={!selectedMethod}
                className="w-full justify-center py-3 text-lg"
              >
                Pay {amount.toLocaleString()} ETB
              </Button>
              <p className="text-center text-xs text-slate-400 mt-3">
                Secured by Adnaki Payments
              </p>
            </div>
          </div>
        );

      case "processing":
        return (
          <div className="py-12 flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-slate-100 border-t-primary rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Smartphone size={24} className="text-primary opacity-50" />
              </div>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-800">
              Processing Payment
            </h3>
            <p className="text-slate-500 mt-2">
              Please wait while we confirm your transaction...
            </p>
            <div className="mt-8 w-full max-w-xs bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-primary animate-pulse w-2/3"></div>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="py-8 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 animate-bounce">
              <CheckCircle size={40} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800">
              Payment Successful!
            </h3>
            <p className="text-slate-500 mt-2 mb-8">
              You have successfully paid{" "}
              <span className="font-bold text-slate-800">
                {amount.toLocaleString()} ETB
              </span>{" "}
              for {itemName}.
            </p>
            <Button onClick={onClose} className="w-full justify-center">
              Done
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === "processing" ? () => {} : onClose}
      title={step === "select" ? "Checkout" : ""}
    >
      {renderContent()}
    </Modal>
  );
};

export default CheckoutModal;
